import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { html } from 'lit/static-html.js';
import { ColumnDef } from '@tanstack/react-table';
import {
  DataTable,
  Grid,
  GridCell,
  IconButton,
  Input,
  PageSection,
  Pagination,
  Spacer,
  Stack,
  TextTitle,
  Tooltip,
} from '@pypestream/design-system';
import { TranslationComponent, useTranslation } from '@pypestream/translations';

import {
  useLoadAsDraftModal,
  useRepublishModal,
  VersionCard,
} from '../components';
import {
  StudioPageProps,
  useProStudioViewerRole,
  useSolutionNameValidation,
} from '../hooks';
import { withStageNavRedirect } from './redirect-into-stage-nav';
import { WithLoadingState } from '../router';
import {
  capitalizeWords,
  formatDateTime,
  getEditableSolutionName,
} from '../utils';
import {
  sendProStudioEvent,
  useProStudioCtxSelector,
} from '../xstate/app.xstate';
import { SolutionVersion } from '../xstate/prostudio.api-service.types';

import './versions.scss';

type VersionsPageProps = StudioPageProps & {
  /** Deploy renders its own expandable versions list instead. */
  hideVersionsTable?: boolean;
};

export const filterUniqueVersions = (
  data: SolutionVersion[]
): SolutionVersion[] => {
  const versionMap = new Map<string, SolutionVersion>();

  data.forEach((item) => {
    const key = `${item.env}-${item.version}`;
    const existing = versionMap.get(key);

    if (!existing || new Date(item.time) > new Date(existing.time)) {
      versionMap.set(key, item);
    }
  });

  return Array.from(versionMap.values());
};

export const mergeVersions = (data: SolutionVersion[]): SolutionVersion[] => {
  const versionMap = new Map<string, SolutionVersion & { env: string }>();

  data.forEach((item) => {
    const key = JSON.stringify({
      picture: item.picture,
      bot: item.bot,
      customer: item.customer,
      email: item.email,
      first_name: item.first_name,
      last_name: item.last_name,
      uuid: item.uuid,
      version: item.version,
    });

    const existing = versionMap.get(key);

    if (existing) {
      const envSet = new Set(
        existing.env.split(', ').concat(item.env.toLowerCase())
      );
      existing.env = Array.from(envSet).sort().join(', ');
      if (new Date(item.time) > new Date(existing.time)) {
        existing.time = item.time;
      }
    } else {
      versionMap.set(key, { ...item, env: item.env.toLowerCase() });
    }
  });

  return Array.from(versionMap.values());
};

/**
 * The Versions page body, exported unwrapped so the 5-stage nav's Deploy
 * stage (`deploy-stage.tsx`) renders the exact same environments + published
 * versions UI rather than a second copy of it.
 */
export const VersionsContent: FC<VersionsPageProps> = ({
  hideVersionsTable,
}) => {
  const [botVersion, setBotVersion] = useState<SolutionVersion | undefined>(
    undefined
  );

  const [isEditMode, setIsEditMode] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const {
    displayName,
    botVersions,
    deployedVersions,
    draftVersion,
    envs,
    botSolutionId,
    solutionId,
  } = useProStudioCtxSelector((ctx) => ({
    botSolutionId: ctx.selectedBot?.id,
    solutionId: ctx.selectedBot?.uniqueId,
    displayName: ctx.selectedBot?.displayName,
    botVersions: filterUniqueVersions(ctx.versions || []),
    deployedVersions: ctx.deployedVersions,
    draftVersion: ctx.draftVersion,
    customerId: ctx.customerId,
    projectId: ctx.project?.projectProStudioSetting?.id,
    botId:
      ctx.customerId && ctx.botName
        ? `${ctx.customerId}.${ctx.botName}`
        : undefined,
    envs: ['draft', ...(ctx.envs ? [...ctx.envs].reverse() : [])],
  }));

  const isViewer = useProStudioViewerRole();

  const [solutionName, setSolutionName] = useState<string>(
    getEditableSolutionName(displayName)
  );

  // Single source for resetting the editable name to the SELECTED solution's name.
  const resetSolutionName = useCallback(
    () => setSolutionName(getEditableSolutionName(displayName)),
    [displayName]
  );

  // Reset whenever the selected solution changes, so a previously edited/viewed
  // solution's name never persists in the input (PSTUDIO-2754). `displayName`
  // does not change while typing, so this never clobbers in-progress edits.
  // `botSolutionId` is a distinct dependency on purpose: two solutions can share
  // the same display name, and in that case only the id changes — without it the
  // reset would not fire on such a switch.
  useEffect(() => {
    resetSolutionName();
  }, [resetSolutionName, botSolutionId]);

  const memoizedBotVersions = useMemo(() => botVersions, [botVersions]);

  const mergedVersions = useMemo(
    () =>
      mergeVersions(memoizedBotVersions).map((ver) => ({
        ...ver,
        id: ver.version,
        name: ver.version,
      })),
    [memoizedBotVersions]
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [mergedVersions.length]);

  const paginatedVersions = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return mergedVersions.slice(start, start + pageSize);
  }, [mergedVersions, currentPage, pageSize]);

  const { hasLengthError, isValidName, getTag, validateSolutionName } =
    useSolutionNameValidation(solutionName);

  const getDeployedVersions = () => {
    const versions = [
      ...Object.entries(deployedVersions || {}),
      ['draft', draftVersion],
    ] as [string, string][];

    for (const env of envs) {
      if (!versions.some(([key]) => key === env)) {
        versions.push([env, '']);
      }
    }

    versions.sort((a, b) => envs.indexOf(a[0]) - envs.indexOf(b[0]));

    return versions;
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const deployedVersionsAsArray = useMemo(getDeployedVersions, [
    deployedVersions,
    draftVersion,
    envs,
  ]);

  const [t] = useTranslation();
  const { republishModal, openModal: openRepublishModal } = useRepublishModal({
    botVersion: botVersion,
  });
  const { loadAsDraftModal, openModal: openLoadAsDraftModal } =
    useLoadAsDraftModal();

  const columns: ColumnDef<SolutionVersion>[] = useMemo(
    () => [
      {
        accessorKey: 'version',
        accessorFn: (row) => row.version,
        header: () =>
          t('pro-studio/versions:table.column1', {
            defaultValue: 'Version',
          }),
        cell: ({ row }) =>
          html`<ps-text-subtitle size="small">
            ${row?.original?.version}
          </ps-text-subtitle>`,
      },
      {
        accessorKey: 'env',
        accessorFn: (row) => row.env,
        header: () =>
          t('pro-studio/versions:table.column2', {
            defaultValue: 'Environment',
          }),
        cell: ({ row }) => {
          if (!row || !row?.original) {
            return null;
          }

          const isActive = deployedVersionsAsArray
            .map((ver) => ({
              version: ver[1],
              env: ver[0].toLowerCase(),
            }))
            .some((ver) => {
              const environments = row.original.env.toLowerCase().split(', ');
              return (
                ver.version === row.original.version &&
                environments.includes(ver.env)
              );
            });

          const { env } = row.original;
          return html`<ps-text-subtitle
            size="small"
            variant=${isActive ? 'primary' : 'tertiary'}
          >
            <!-- ${t(`pro-studio/common:environments.${env.toLowerCase()}`, {
              defaultValue: env,
            })} -->
            ${capitalizeWords(env)}
          </ps-text-subtitle>`;
        },
      },
      {
        accessorKey: 'time',
        accessorFn: (row) => row.time,
        header: () =>
          t('pro-studio/versions:table.column3', {
            defaultValue: 'Last Published',
          }),
        cell: ({ row }) => {
          if (!row || !row?.original) {
            return null;
          }

          const { first_name, last_name, picture, time } = row.original;
          const userName = `${first_name} ${last_name}`;
          return html`<ps-stack alignItems="center" gutter="2xsmall" nowrap>
            ${userName.trim()
              ? html`<ps-avatar
                    size="small"
                    src=${picture}
                    label=${userName}
                  ></ps-avatar>
                  <ps-text-body truncate>${userName}</ps-text-body>
                  <ps-text-body truncate
                    >at ${time ? formatDateTime(time) : ''}
                  </ps-text-body>`
              : null}
          </ps-stack>`;
        },
      },
      {
        id: 'actions',
        header: () =>
          html`<ps-text-body style="opacity: 0">Actions</ps-text-body>`,
        cell: ({ row }) =>
          html`<ps-stack
            alignItems="center"
            justifyContent="end"
            gutter="xsmall"
            nowrap
          >
            <!-- <ps-button
            variant="secondary"
            size="small"
            @click=${openLoadAsDraftModal}
            ><ps-icon name="refresh" slot="prefix" size="xsmall"></ps-icon>${t(
              'pro-studio/versions:table.buttons.load_as_draft',
              {
                defaultValue: 'Load as draft',
              }
            )}</ps-button
          > -->
            ${!isViewer
              ? html`<ps-tooltip
                  content="Republishing a version will not replace nodes on the workflows page"
                >
                  <ps-button
                    variant="secondary"
                    size="small"
                    @click=${() => {
                      setBotVersion(row?.original);
                      openRepublishModal();
                    }}
                    ><ps-icon
                      name="deploy"
                      slot="prefix"
                      size="xsmall"
                    ></ps-icon>
                    ${t('pro-studio/versions:table.buttons.republish', {
                      defaultValue: 'Republish',
                    })}
                  </ps-button>
                </ps-tooltip>`
              : null}

            <ps-tooltip
              content=${t('pro-studio/versions:vocabulary.download_solution', {
                defaultValue: 'Download Solution',
              })}
            >
              <ps-icon-button
                name="download"
                size="small"
                @click=${() => {
                  sendProStudioEvent({
                    type: 'getVersionFile',
                    version: row?.original.version,
                  });
                }}
              ></ps-icon-button>
            </ps-tooltip>
          </ps-stack>`,
        meta: {
          align: 'end',
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      t,
      deployedVersionsAsArray,
      isViewer,
      openRepublishModal,
      openLoadAsDraftModal,
    ]
  );

  const copyDuploCloudID = (id: string) => {
    sendProStudioEvent({
      type: 'copyToClipboard',
      value: id,
      message:
        t('pro-studio/versions:duplo_cloud_id.message', {
          defaultValue: 'DuploCloud ID copied to clipboard',
        }) || '',
    });
  };

  const onSaveSolutionName = () => {
    if (!solutionName.trim() || !botSolutionId || !solutionId) {
      return;
    }

    setIsEditMode(false);

    if (solutionName.trim() === displayName) {
      return;
    }

    sendProStudioEvent({
      type: 'updateSolutionName',
      name: solutionName,
      solutionId,
    });
  };

  return (
    <React.Fragment>
      <Stack gutter="xsmall">
        {isEditMode ? (
          <Stack direction="column" gutter="xsmall">
            <Input
              test-id="ps-versions-page-solution-name-input"
              value={solutionName}
              name="solution-name"
              // @ts-expect-error - TODO: onInput event type has no value property
              onInput={(e: React.ChangeEvent<HTMLInputElement>) => {
                setSolutionName(validateSolutionName(e.target.value));
              }}
              size="small"
              className="c-versions-page-title-input"
              hasError={hasLengthError}
              helpText={
                hasLengthError
                  ? (t(
                      'pro-studio/homepage:modal.form.name.validation.length',
                      {
                        defaultValue: 'Use 1–100 characters.',
                      }
                    ) ?? undefined)
                  : undefined
              }
            />
            <Stack gutter="small">
              {getTag(
                t('pro-studio/homepage:modal.form.name.validation.uniqueness', {
                  defaultValue: 'Uniqueness',
                }),
                'passedUniquenessCheck'
              )}
            </Stack>
          </Stack>
        ) : (
          <TextTitle test-id="ps-versions-page-title" size="small">
            {displayName}
          </TextTitle>
        )}
        <div className="c-versions-page-title-controls">
          <Stack gutter="xsmall" alignItems="center">
            {isEditMode ? (
              <>
                <IconButton
                  test-id="ps-versions-page-confirm-new-solution-name-button"
                  onClick={onSaveSolutionName}
                  name="check"
                  size="small"
                  disabled={!isValidName}
                />
                <IconButton
                  test-id="ps-versions-page-cancel-edit-solution-name-button"
                  onClick={() => {
                    setIsEditMode(false);
                    resetSolutionName();
                  }}
                  name="close"
                  size="small"
                />
              </>
            ) : (
              <>
                {!isViewer && (
                  <IconButton
                    test-id="ps-versions-page-edit-solution-name-button"
                    onClick={() => {
                      resetSolutionName();
                      setIsEditMode(true);
                    }}
                    name="pencil"
                    size="small"
                    disabled={false}
                  />
                )}
                <Tooltip
                  test-id="ps-versions-page-duplo-cloud-id-copy-tooltip"
                  content={
                    t('pro-studio/versions:duplo_cloud_id.copy', {
                      defaultValue: 'Copy DuploCloud ID',
                    }) || ''
                  }
                >
                  <IconButton
                    disabled={!botSolutionId}
                    test-id="ps-versions-page-duplo-cloud-id-copy-button"
                    onClick={() => {
                      if (!botSolutionId) {
                        return;
                      }

                      copyDuploCloudID(botSolutionId);
                    }}
                    name="copy"
                    size="small"
                  />
                </Tooltip>
              </>
            )}
          </Stack>
        </div>
        {/** <TextCaption test-id="ps-versions-page-org-name">
          for {orgName}
        </TextCaption>
        **/}
      </Stack>
      <Spacer size="large" />
      <TextTitle size="xsmall" test-id="ps-versions-page-cards-title">
        <TranslationComponent i18nKey="pro-studio/versions:cards.title">
          Environments
        </TranslationComponent>
      </TextTitle>
      <Spacer />
      <Grid gutter="large" rowGutter="large" alignItems="stretch">
        {deployedVersionsAsArray.map(([env, version]) => {
          const blankSolution = {
            version,
            env,
            time: '',
            first_name: '',
            last_name: '',
            picture: '',
            uuid: '',
            bot: '',
            customer: '',
            email: '',
          };

          const solution =
            env === 'draft' || !version
              ? blankSolution
              : memoizedBotVersions?.find(
                  (item) => item.version === version && item.env === env
                ) || blankSolution;

          return (
            <GridCell key={env} xsmall="12" medium="6" large="4">
              <VersionCard key={env} solution={solution} isViewer={isViewer} />
            </GridCell>
          );
        })}
      </Grid>
      {!hideVersionsTable && (
        <>
          <Spacer size="large" />
          <TextTitle size="xsmall" test-id="ps-versions-page-table-title">
            <TranslationComponent i18nKey="pro-studio/versions:table.title">
              Published Versions
            </TranslationComponent>
          </TextTitle>
          <Spacer />
          <PageSection size="small">
            <DataTable
              test-id="ps-versions-page-versions-table"
              size="small"
              columns={columns}
              actionable={false}
              data={paginatedVersions}
            />
            {mergedVersions.length > 10 && (
              <>
                <Spacer />
                <Pagination
                  test-id="ps-versions-page-pagination"
                  total={mergedVersions.length}
                  pageSize={pageSize}
                  currentPage={currentPage}
                  onChange={({ detail }) => {
                    setCurrentPage(detail.page);
                    setPageSize(detail.pageSize);
                  }}
                  onShowSizeChange={({ detail }) => {
                    setCurrentPage(detail.page);
                    setPageSize(detail.pageSize);
                  }}
                />
              </>
            )}
          </PageSection>
        </>
      )}

      {loadAsDraftModal}
      {republishModal}
    </React.Fragment>
  );
};

const VersionsPageWithLoadingState = WithLoadingState(VersionsContent);

/**
 * The solution-root route. With `bobcat-five-stage-nav` on there is no
 * standalone Versions page — its features live in the Deploy stage — so the
 * root lands inside the 5-stage shell instead, always on Design, the
 * prototype's "persistent front door". That includes right after the
 * conversational-create flow: its pending canvas landing still pre-selects
 * Canvas inside Build (`builder.tsx`), so Build opens on the new agents.
 * Flag off: the real page, exactly as before.
 */
export const Versions: FC<VersionsPageProps> = withStageNavRedirect(
  VersionsPageWithLoadingState,
  { stage: 'design' }
);
